import React, { useState } from 'react';
import 'dayjs/locale/de'; // Deutsche Sprache laden
import {
   Dialog,
   DialogTitle,
   DialogContent,
   DialogActions,
   TextField,
   Button,
   Box,
   FormControl,
   InputLabel,
   Select,
   MenuItem,
   Typography,
   RadioGroup,
   Radio,
   FormControlLabel,
   FormLabel,
   Chip,
   Stack,
   ButtonGroup,
   IconButton,
   Stepper,
   Step,
   StepLabel
} from '@mui/material';
import DoneIcon from '@mui/icons-material/Done';
import RemoveIcon from '@mui/icons-material/Remove';
import CloseIcon from '@mui/icons-material/Close';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import { saveCheck } from '../strapi/queries';

dayjs.locale('de');

// Predefined norms for different roles
const NORMS = {
   bergretter: [
      'EN 12277', 'EN 1891 Typ A', 'EN 12841', 'EN 341', 'EN 12492', 'EN 354', 'EN 566'
   ],
   bergfuehrer: [
      'EN 12277', 'EN 892', 'EN 12492', 'EN 12275', 'EN 566', 'EN 13089', 'UIAA 101', 'UIAA 105'
   ],
   industriekletterer: [
      'EN 361', 'EN 358', 'EN 813', 'EN 1891 Typ A', 'EN 12841', 'EN 397', 'EN 354', 'EN 795', 'EN 360'
   ]
};

/**
 * Modal component for checking items
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the modal is open
 * @param {Function} props.onClose - Function to call when the modal is closed
 * @param {Array} props.selectedItems - Array of selected items to check
 * @param {Function} props.onSuccess - Function to call when a check is successfully created
 * @returns {JSX.Element} - The modal component
 */
const CheckModal = ({ open, onClose, selectedItems, containerId, onSuccess }) => {
   const steps = ['Prüfdaten', 'Norm', 'Kommentar & Ergebnis'];
   // State for form values
   const [checkDate, setCheckDate] = useState(dayjs());
   const [nextCheckDate, setNextCheckDate] = useState(dayjs().add(1, 'year'));
   const [normType, setNormType] = useState('bergretter');
   const [customNorm, setCustomNorm] = useState('');
   const [selectedNorm, setSelectedNorm] = useState('Sichtkontrolle');
   const [comment, setComment] = useState('');
   const [checkStateSelected, setCheckStateSelected] = useState('success');
   const [isSubmitting, setIsSubmitting] = useState(false);
   const [error, setError] = useState(null);
   const [normInputType, setNormInputType] = useState('predefined');
   const [activeStep, setActiveStep] = useState(0); // New state for current step

   const handleClose = () => {
      resetForm();
      onClose();
   };

   const resetForm = () => {
      setCheckDate(dayjs());
      setNextCheckDate(dayjs().add(1, 'year'));
      setNormType('bergretter');
      setCustomNorm('');
      setSelectedNorm('');
      setComment('');
      setCheckStateSelected('success');
      setError(null);
      setNormInputType('predefined');
      setActiveStep(0); // Reset step on close
   };

   const handleNormTypeChange = (event) => {
      setNormType(event.target.value);
      setSelectedNorm(''); // Reset selected norm when changing norm type
   };

   const handleNormChange = (event) => {
      setSelectedNorm(event.target.value);
   };

   const handleNormInputTypeChange = (event) => {
      setNormInputType(event.target.value);
      // Reset values when switching between predefined and custom
      if (event.target.value === 'predefined') {
         setCustomNorm('');
      } else {
         setSelectedNorm('');
      }
   };

   const handleNext = () => {
      // Validation for Step 1 (Check Dates)
      if (activeStep === 0) {
         if (!checkDate || !nextCheckDate) {
            setError('Bitte geben Sie sowohl das Prüfdatum als auch das nächste Prüfdatum ein.');
            return;
         }
      }
      // Validation for Step 2 (Norm Selection)
      if (activeStep === 1) {
         if (normInputType === 'predefined' && !selectedNorm) {
            setError('Bitte wählen Sie eine Norm aus.');
            return;
         }
         if (normInputType === 'custom' && !customNorm.trim()) {
            setError('Bitte geben Sie eine benutzerdefinierte Norm ein.');
            return;
         }
      }
      setError(null); // Clear any previous errors
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
   };

   const handleBack = () => {
      setActiveStep((prevActiveStep) => prevActiveStep - 1);
      setError(null); // Clear errors when going back
   };

   const handleSubmit = async () => {
      // This function is now only called on the final step


      setIsSubmitting(true);
      setError(null);

      try {
         // Prepare items data
         const itemsData = selectedItems
            .filter(item => item.type === 'containerItem' && !item.ids) // Nur einzelne Items
            .map(item => ({
               type: 'containerItem',
               id: item.id,
               name: item.name,
               category: item.category
            }));

         const checkData = {
            items: itemsData,
            check_date: checkDate,
            next_check_date: nextCheckDate,
            norm: normInputType === 'predefined' ? selectedNorm : customNorm,
            comment: comment,
            result: checkStateSelected,
            container_id: containerId
         };

         const result = await saveCheck(checkData);

         if (result && result.error) {
            setError(result.error.message || 'Fehler beim Speichern der Prüfung.');
         } else {
            handleClose();
            if (onSuccess) onSuccess();
         }
      } catch (err) {
         setError('Ein Fehler ist aufgetreten. Bitte versuchen Sie es später erneut.');
         console.error('Error saving check:', err);
      } finally {
         setIsSubmitting(false);
      }
   };

   return (
      <Dialog
         open={open}
         onClose={handleClose}
         maxWidth="sm"
         fullWidth
         sx={{
            '& .MuiDialog-paper': {
               maxHeight: '90vh',
            }
         }}
      >
         <DialogTitle>Prüfung durchführen</DialogTitle>
         <DialogContent sx={{ paddingBottom: "5px" }}>
            <Box sx={{ width: '100%', mt: 1, paddingBottom: "0px" }}>
               <Stepper activeStep={activeStep} alternativeLabel >
                  {steps.map((label) => (
                     <Step key={label}>
                        <StepLabel>{label}</StepLabel>
                     </Step>
                  ))}
               </Stepper>
               <Box sx={{ pt: 1 }}>
                  {activeStep === 0 && (
                     <>
                        {/* Selected Items Display */}
                        <Typography variant="subtitle1" gutterBottom>
                           Ausgewählte Items ({selectedItems.filter(item => item.type === 'containerItem' && !item.ids).length}):
                        </Typography>
                        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1, mb: 3 }}>
                           {selectedItems.filter(item => item.type === 'containerItem' && !item.ids).map((item, index) => (
                              <Chip
                                 key={index}
                                 label={item.name}
                                 color="primary"
                                 variant="outlined"
                              />
                           ))}
                        </Stack>

                        {/* Check Date */}
                        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="de">
                           <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                              <DatePicker
                                 label="Prüfdatum"
                                 value={checkDate}
                                 onChange={(newValue) => setCheckDate(newValue)}
                                 slotProps={{
                                    textField: {
                                       fullWidth: true,
                                       variant: 'outlined',
                                       sx: { flexGrow: 1, minWidth: '200px' }
                                    }
                                 }}
                              />
                              <DatePicker
                                 label="Nächstes Prüfdatum"
                                 value={nextCheckDate}
                                 onChange={(newValue) => setNextCheckDate(newValue)}
                                 slotProps={{
                                    textField: {
                                       fullWidth: true,
                                       variant: 'outlined',
                                       sx: { flexGrow: 1, minWidth: '200px' }
                                    }
                                 }}
                              />
                           </Box>
                        </LocalizationProvider>
                     </>
                  )}

                  {activeStep === 1 && (
                     <>
                        {/* Norm Selection */}
                        <Box sx={{ mb: 3 }}>
                           <Typography variant="subtitle1" gutterBottom>
                              Norm-Auswahl:
                           </Typography>
                           <FormControl component="fieldset">
                              <RadioGroup
                                 row
                                 name="norm-input-type"
                                 value={normInputType}
                                 onChange={handleNormInputTypeChange}
                              >
                                 <FormControlLabel value="predefined" control={<Radio />} label="Vordefinierte Norm" />
                                 <FormControlLabel value="custom" control={<Radio />} label="Benutzerdefinierte Norm" />
                              </RadioGroup>
                           </FormControl>
                        </Box>

                        {normInputType === 'predefined' ? (
                           <Box sx={{ mb: 3 }}>
                              <FormControl fullWidth sx={{ mb: 2 }}>
                                 <InputLabel id="norm-type-label">Norm-Kategorie</InputLabel>
                                 <Select
                                    labelId="norm-type-label"
                                    id="norm-type-select"
                                    value={normType}
                                    label="Norm-Kategorie"
                                    onChange={handleNormTypeChange}
                                 >
                                    <MenuItem value="bergretter">Bergretter</MenuItem>
                                    <MenuItem value="bergfuehrer">Bergführer (UIAGM/IFMGA)</MenuItem>
                                    <MenuItem value="industriekletterer">Industriekletterer / Höhenretter</MenuItem>
                                 </Select>
                              </FormControl>

                              <FormControl fullWidth>
                                 <InputLabel id="norm-label">Norm</InputLabel>
                                 <Select
                                    labelId="norm-label"
                                    id="norm-select"
                                    value={selectedNorm}
                                    label="Norm"
                                    onChange={handleNormChange}
                                 >
                                    {NORMS[normType].map((norm) => (
                                       <MenuItem key={norm} value={norm}>{norm}</MenuItem>
                                    ))}
                                 </Select>
                              </FormControl>
                           </Box>
                        ) : (
                           <Box sx={{ mb: 3 }}>
                              <TextField
                                 fullWidth
                                 label="Benutzerdefinierte Norm"
                                 value={customNorm}
                                 onChange={(e) => setCustomNorm(e.target.value)}
                                 variant="outlined"
                              />
                           </Box>
                        )}
                     </>
                  )}

                  {activeStep === 2 && (
                     <>
                        {/* Comment */}
                        <Typography variant="subtitle1" gutterBottom>
                           Kommentar:
                        </Typography>
                        <TextField
                           fullWidth
                           multiline
                           rows={4}
                           value={comment}
                           onChange={(e) => setComment(e.target.value)}
                           variant="outlined"
                           sx={{ mb: 3 }}
                        />
                        {/* Result Buttons */}
                        <Typography variant="subtitle1" gutterBottom>
                           Prüfergebnis:
                        </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                           <ButtonGroup variant="contained" aria-label="outlined primary button group">
                              <IconButton
                                 color="success"
                                 onClick={() => setCheckStateSelected('success')}
                                 sx={{
                                    border: "3px solid " + (checkStateSelected === 'success' ? '#10527e' : 'white'),
                                    margin: "5px"
                                 }}                           >
                                 <DoneIcon />
                              </IconButton>
                              <IconButton
                                 color="warning"
                                 onClick={() => setCheckStateSelected('noresult')}
                                 sx={{
                                    border: "3px solid " + (checkStateSelected === 'noresult' ? '#10527e' : 'white'),
                                    margin: "5px"
                                 }}
                              >
                                 <RemoveIcon />
                              </IconButton>
                              <IconButton
                                 color="error"
                                 onClick={() => setCheckStateSelected('fail')}
                                 sx={{
                                    border: "3px solid " + (checkStateSelected === 'fail' ? '#10527e' : 'white'),
                                    margin: "5px"
                                 }}
                              >
                                 <CloseIcon />
                              </IconButton>
                           </ButtonGroup>
                        </Box>
                        {checkStateSelected && (
                           <Typography variant="subtitle1" align="center" >
                              {checkStateSelected === 'success' && 'Bestanden'}
                              {checkStateSelected === 'noresult' && 'Ohne Ergebnis'}
                              {checkStateSelected === 'fail' && 'Nicht bestanden'}
                           </Typography>
                        )}
                     </>
                  )}

                  {error && (
                     <Typography color="error" sx={{ mt: 2 }}>
                        {error}
                     </Typography>
                  )}
               </Box>
            </Box>
         </DialogContent>
         <DialogActions>
            <Button
               sx={{ color: "#10527e" }}
               onClick={handleClose}
               disabled={isSubmitting}>
               Abbrechen
            </Button>
            {activeStep > 0 && (
               <Button
                  sx={{ color: "#10527e" }}
                  onClick={handleBack}
                  disabled={isSubmitting}>
                  Zurück
               </Button>
            )}
            {activeStep < 2 && (
               <Button
                  sx={{
                     backgroundColor: '#10527e',
                     color: 'white',
                     '&:hover': {
                        backgroundColor: '#282c34',
                     },
                     '&.Mui-disabled': {
                        backgroundColor: '#cccccc',
                        color: '#666666'
                     }
                  }}
                  onClick={handleNext}
                  disabled={isSubmitting}>
                  Weiter
               </Button>
            )}
            {activeStep === 2 && (
               <Button
                  onClick={handleSubmit}
                  sx={{
                     backgroundColor: '#10527e',
                     color: 'white',
                     '&:hover': {
                        backgroundColor: '#282c34',
                     },
                     '&.Mui-disabled': {
                        backgroundColor: '#cccccc',
                        color: '#666666'
                     }
                  }}
                  disabled={isSubmitting}
               >
                  {isSubmitting ? 'Speichern...' : 'Speichern'}
               </Button>
            )}
         </DialogActions>
      </Dialog>
   );
};

export default CheckModal;